import { noop } from '@capsicum/common';
import { type AnimationProps, motion, useDragControls, useMotionValue, useMotionValueEvent } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { type FC } from 'react';
import ClickAwayListener from 'react-click-away-listener';

import { useBodyScrollLock } from '../../../hooks/helpers/useBodyScrollLock';
import { useWindowSize } from '../../../hooks/helpers/useWindowSize';
import { type MobileModalWrapperProps } from '../../../models/modal';

import * as S from './MobileModalWrapper.styled';
import { IsModalDraggableAtom } from './ModalDraggableAtom';

const foregroundVariants: AnimationProps['variants'] = {
	initial: {
		bottom: '-100%'
	},
	view: {
		bottom: 0,
		transition: {
			duration: 0.5
		}
	}
};

export const MobileModalWrapper: FC<MobileModalWrapperProps> = ({
	background,
	child,
	closeIconColor,
	closeModal,
	detached = true,
	footer,
	fullHeight = false,
	inView,
	isClosable = true,
	isDraggable,
	justClosed,
	subHeader,
	testID = 'common-mobileModalWrapper'
}) => {
	useBodyScrollLock();
	const isModalDraggable = useAtomValue(IsModalDraggableAtom);
	const { dimensions } = useWindowSize();
	const modalHeight = dimensions.height * 0.9;
	const isDragEnabled = typeof isDraggable === 'boolean' ? isDraggable : isModalDraggable;
	const y = useMotionValue(0);
	const controls = useDragControls();

	// When the user slides the alert, we want to dismiss
	useMotionValueEvent(y, 'change', latest => {
		if (isClosable && latest > 300) {
			closeModal?.();
		}
	});

	return (
		<S.Container aria-label={testID} data-testid={testID} role="dialog">
			<S.Background
				animate={inView && !justClosed ? 'show' : 'hide'}
				as={motion.div}
				initial="hide"
				variants={{
					hide: {
						opacity: 0
					},
					show: {
						opacity: 0.8
					}
				}}
			/>
			<ClickAwayListener onClickAway={isClosable ? () => closeModal?.() : noop}>
				<S.Foreground
					$background={background}
					$detached={detached}
					$fullHeight={fullHeight}
					$modalHeight={modalHeight}
					$showCloseButton={isClosable}
					animate={inView && !justClosed ? 'view' : 'initial'}
					aria-label={testID + '-foreground'}
					as={motion.div}
					data-testid={testID + '-foreground'}
					drag={inView && isClosable && isDragEnabled ? 'y' : undefined}
					dragConstraints={
						inView
							? {
									bottom: 0,
									top: 0
								}
							: {}
					}
					dragControls={controls}
					dragElastic={{ bottom: 1 }}
					dragListener={false}
					dragTransition={{ bounceDamping: 20, bounceStiffness: 200 }}
					initial="initial"
					style={{
						y
					}}
					variants={foregroundVariants}
				>
					<S.Header onPointerDown={e => controls.start(e)}>
						{isClosable && (
							<S.StyledChevronIcon
								$color={closeIconColor}
								aria-label={`${testID}-closeIcon`}
								data-testid={`${testID}-closeIcon`}
								onClick={() => closeModal?.()}
							/>
						)}
					</S.Header>
					{subHeader && <S.SubHeader>{subHeader}</S.SubHeader>}
					<S.Content>{child}</S.Content>
					<S.Footer>{footer}</S.Footer>
				</S.Foreground>
			</ClickAwayListener>
		</S.Container>
	);
};
